<template>
  <div>
    <v-card class="mx-auto" max-width="400">
      <v-tabs v-model="mode" background-color="transparent" grow>
        <v-tab :ripple="false" href="#register">
          <span class="text-uppercase">Register</span>
        </v-tab>
        <v-tab :ripple="false" href="#login">
           <span class="text-uppercase">Login</span>
        </v-tab>
      </v-tabs>
      
      <div class="px-8 py-4">
        <form :key="mode" :name="mode" @submit.prevent="onSubmit">
            <v-text-field
              dense
              filled
              rounded
              class="pb-1"
              v-if="isRegister"
              v-model="displayName"
              type="text"
              :error-messages="displayNameErrors"
              placeholder="Display Name"
              label="Display Name"
              name="nickname"
              autocomplete="nickname"
              key="displayName"
              required
              @input="$v.displayName.$touch()"
              @blur="$v.displayName.$touch()"
            />

            <v-text-field
              dense
              filled
              rounded
              class="pb-1"
              v-model="email"
              type="email"
              :error-messages="emailErrors"
              placeholder="Email"
              label="Email"
              name="email"
              autocomplete="username"
              key="email"
              required
              @input="$v.email.$touch()"
              @blur="$v.email.$touch()"
            />

            <v-text-field
              dense
              filled
              rounded
              class="pb-1"
              v-model="password"
              type="password"
              :error-messages="passwordErrors"
              placeholder="*******"
              label="Password"
              name="password"
              :autocomplete="isRegister ? 'new-password' : 'current-password'"
              key="password"
              required
              @input="$v.password.$touch()"
              @blur="$v.password.$touch()"
            />

            <v-text-field
              dense
              filled
              rounded
              v-if="isRegister"
              v-model="passwordRepeat"
              type="password"
              :error-messages="passwordRepeatErrors"
              placeholder="*******"
              label="Repeat password"
              name="new-password-repeat"
              autocomplete="new-password"
              key="passwordRepeat"
              required
              @input="$v.passwordRepeat.$touch()"
              @blur="$v.passwordRepeat.$touch()"
            />

            <div v-if="isRegister && policies.length">
              <v-checkbox v-for="policy in policies" :key="policy.identifier" class="ml-6 mb-0 mt-0 checkbox" @change="policyCheck(policy, $event)" required>
                <template v-slot:label>
                  <div>
                    <small>
                      I have read and accept the
                      <a  href="#" @click.stop="showPolicyDialog = true; selectedPolicy = policy">
                      {{policy.identifier}}
                      </a>
                    </small>
                  </div>
                </template>
              </v-checkbox>
            </div>

            <v-checkbox v-if="isRegister" v-model="allowMarketingEmails" class="ml-6 mb-4 mt-0 checkbox">
              <template v-slot:label>
                <small>Allow Marketing Emails</small>
              </template>
            </v-checkbox>

            <p v-if="isRegister && verificationTokenSent">Apex Connect requires a verified email. A verification code has been sent to your email</p>
            <v-btn 
              rounded
              class="mb-4"
              v-if="isRegister && verificationTokenSent"
              @click="sendVerificationToken">
                Resend code
            </v-btn>
            <v-text-field 
              dense
              filled
              rounded
              v-if="isRegister && verificationTokenSent"
              v-model="verificationToken"
              label="Verification Token"
              name="verification-token"
              autocomplete="one-time-code"
              key="verificationToken"
              inputmode="numeric"
              required
              :error-messages="verificationTokenErrors"
              @input="$v.verificationToken.$touch()"
              @blur="$v.verificationToken.$touch()"
            />

            <div class="d-flex justify-end">
              <v-btn
                v-if="!isRegister"
                @click="forgotPassword"
                type="button"
                :disabled="$v.email.$invalid || forgotPasswordLoading"
                :loading="forgotPasswordLoading">
                  Forgot Password
              </v-btn>

              <span v-if="!isRegister" class="px-1"></span>
              <v-btn v-if="!isRegister" type="submit" :disabled="$v.$invalid">Login</v-btn>

              <v-btn v-if="isRegister && !verificationTokenSent" type="submit">Continue</v-btn>
              <v-btn v-if="isRegister && verificationTokenSent" type="submit">Create Account</v-btn>
            </div>
          </form>
      </div>
    </v-card>
    
    <v-dialog v-model="loginFailure" width="400" :fullscreen="$vuetify.breakpoint.mobile">
      <v-card>
        <v-card-title class="headline">{{loginErrorHeader}}</v-card-title>

        <v-card-text>{{loginErrorText}}</v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="toggleInvalidEmailPassword()">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="restoreAccountDialog" persistent max-width="500" :fullscreen="$vuetify.breakpoint.mobile">
      <v-card>
        <v-card-title>
          <span class="headline">Restore Account?</span>
        </v-card-title>
        <v-card-text>
          <p>You account will be deleted at {{deleteTime | formatTime}}.</p>
          <p>Do you want to restore it?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn type="button" @click.native="restoreAccountDialog = false;">No</v-btn>
          <v-btn type="button" @click="restoreAccount()">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPolicyDialog" max-width="800" :fullscreen="$vuetify.breakpoint.mobile">
      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showPolicyDialog = false">close</v-btn>
        </v-card-actions>
        <v-card-text>
          <div v-html="selectedPolicy.text" class="policy"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<style scoped>
.checkbox {
  height: 34px;
}

/* No-op animation purely so Chrome's autofill restyle fires animationstart. */
@keyframes onAutofillStart {
  from {
    opacity: 1;
  }
  to {
    opacity: 1;
  }
}

::v-deep input:-webkit-autofill {
  animation-name: onAutofillStart;
}
</style>
<script>
import {
  required,
  email,
  minLength,
  maxLength,
  sameAs,
} from "vuelidate/lib/validators";

import router from './../router';

import { createFormErrors, isProfane } from "../util";

import { DateTime } from "luxon";

// native input name -> data key, for reading back autofilled values
const FIELD_BY_NAME = {
  email: "email",
  password: "password",
  nickname: "displayName",
  "new-password-repeat": "passwordRepeat",
};

export default {
  name: "LoginComponent",
  props: {
    initialMode: { type: String, required: true },
    policies: { type: Array, required: false }
  },
  data() {
    return {
      // fields
      email: "",
      password: "",
      passwordRepeat: "",
      allowMarketingEmails: false,
      acceptsPP: false,
      acceptsEULA: false,
      displayName: "",
      verificationToken: "",
      // control
      prevUrl: "",
      mode: this.initialMode,
      deleteTime: null,
      verificationTokenSent: false,
      // dialogs
      restoreAccountDialog: false,
      loginFailure: false,
      loginErrorHeader: "",
      loginErrorText: "",
      // loaders
      forgotPasswordLoading: false,
      selectedPolicy: {},
      showPolicyDialog: false,
      referralCode: ""
    };
  },
  validations() {
    if (this.mode == "login") {
      return {
        email: {
          required,
          email,
        },
        password: {
          required,
          minLength: minLength(1),
        },
      };
    }

    return {
      displayName: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(45),
        isProfane: isProfane,
      },
      email: {
        required,
        maxLength: maxLength(512),
        email,
      },
      password: {
        required,
        minLength: minLength(4),
      },
      passwordRepeat: {
        required,
        minLength: minLength(4),
        sameAs: sameAs("password"),
      },
      acceptsPP: {
        required,
        sameAs: sameAs( () => true )
      },
      acceptsEULA: {
        required,
        sameAs: sameAs( () => true )
      },
      verificationToken: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      }
    };
  },
  created() {
    this.getReferralCode();
  },
  mounted() {
    // Bound on the root, not the form: Vuetify strips external `change`
    // listeners off its inner input (VTextField.genInput), and the form element
    // itself is replaced whenever `mode` changes. The root outlives both.
    this.$el.addEventListener("input", this.onNativeFieldEvent, true);
    this.$el.addEventListener("change", this.onNativeFieldEvent, true);
    this.$el.addEventListener("animationstart", this.onAutofillAnimation, true);
    this.$nextTick(this.syncAutofilledFields);
  },
  beforeDestroy() {
    this.$el.removeEventListener("input", this.onNativeFieldEvent, true);
    this.$el.removeEventListener("change", this.onNativeFieldEvent, true);
    this.$el.removeEventListener("animationstart", this.onAutofillAnimation, true);
  },
  watch: {
    // Switching tabs mounts a different set of inputs, which the manager may
    // fill again.
    mode() {
      this.$nextTick(this.syncAutofilledFields);
    },
  },
  computed: {
    isRegister() {
      return this.mode == "register";
    },
    // validation errors
    displayNameErrors() {
      return createFormErrors(this.$v.displayName);
    },
    emailErrors() {
      return createFormErrors(this.$v.email);
    },
    passwordErrors() {
      return createFormErrors(this.$v.password);
    },
    passwordRepeatErrors() {
      return createFormErrors(this.$v.passwordRepeat);
    },
    verificationTokenErrors() {
      return createFormErrors(this.$v.verificationToken)
    },
    acpConfig: () => {
      return window.ACP_CONFIG;
    },
  },
  methods: {
    policyCheck(pol, e) { 
      if (pol.identifier === 'EULA') this.acceptsEULA = e;
      if (pol.identifier === 'PRIVACY POLICY') this.acceptsPP = e;
     },
    onSubmit() {
      if (this.mode == "login") {
        this.login();
      } else {
        if (!this.verificationTokenSent) {
          this.sendVerificationToken();
        } else {
          this.register();
        }
      }
    },
    restoreAccount() {
      this.$store
        .dispatch("auth/restoreAccount", {
          email: this.email,
          password: this.password,
        })
        .then(() => {
          this.login();
        });
    },
    login() {
      this.$store
        .dispatch("auth/login", {
          email: this.email,
          password: this.password,
          game: "secondextinction",
        })
        .catch((err) => {
          console.error("login failure", err)
          if (err.response.status == 423) {
            // Account Deleted.
            this.deleteTime = DateTime.fromISO(
              err.response.data.delete_time
            ).plus({ days: 1 });
            this.restoreAccountDialog = true;
          } else {
            this.loginErrorHeader = "Could not log in.";
            this.loginErrorText = "Invalid email and/or password";
            this.loginFailure = true;
            const from = router.currentRoute.query['from'] || '/settings';
            if (from === "/games") {
              this.prevUrl = "/register?from=%2Fgames"
            }
          }
        });
    },
    sendVerificationToken() {
      this.$store
        .dispatch("auth/createVerificationToken", {
          email: this.email,
        })
        .then(() => {
          this.verificationTokenSent = true;
        })
        .catch((err) => {
          const errorCode = err.response.data.code
          switch(errorCode) {
            case 2: 
              this.loginErrorHeader = "Email already in use"
              this.loginErrorText = "This email is already in use. Please use another email or login to the existing account. If you have forgotten your password, you can reset it in the login form."
              break;
            case 4:
              this.loginErrorHeader = "Invalid email address";
              this.loginErrorText = "Invalid email, please change it and try again.";
              break;
            default:
              this.loginErrorHeader = "Could not create verification code";
              this.loginErrorText = "Could not ceate verification code. Please try again later.";
          }
          this.loginFailure = true;
          return
        })
    },
    register() {
      this.$store
        .dispatch("auth/register", {
          info: {
            email: this.email,
            password: this.password,
            promo_email_ok: this.allowMarketingEmails,
            nick_name: this.displayName,
            referral_code: this.referralCode,
            verification_token: this.verificationToken,
          },
          policies: this.policies,
          language: this.language
        })
        .catch((err) => {
            const message = err.response.data.message
            if (err.response) {
              if (err.response.status == 400) {
                if (message === "Invalid verification token") {
                  this.loginErrorText = "Invalid verification code, please try again.";
                } else {
                  this.loginErrorText = "Bad request, some of the information you submitted is invalid. Please check your input and try again.";
                }
              } else if (err.response.status == 409) {
                // email already in use, should not happen anymor since we verify the email before we get here.
                this.loginErrorText = "An account with this email address is already registered.";
              } else {
                this.loginErrorText = "Unknown server error. Please try again later.";
              }

            } 
            
            this.loginErrorHeader = "Could not create account.";
            this.loginFailure = true;
        });

      
    },
    toggleRegister() {
      this.mode = "register";
    },
    toggleLogin() {
      this.mode = "login";
    },
    toggleInvalidEmailPassword() {
      this.loginFailure = false
      if(this.prevUrl === "/register?from=%2Fgames")
      router.replace(this.prevUrl)

    },
    forgotPassword() {
      this.forgotPasswordLoading = true;
      this.$store
        .dispatch("auth/forgotPassword", { email: this.email })
        .then(() => {
          this.loginErrorHeader = "Reset password link sent.";
          this.loginFailure = true;
          this.loginErrorText = "An email with reset instructions have been sent. If you can't see it within an hour, check your spam folder.";
          this.forgotPasswordLoading = false;

        });
    },
    // A password manager writes straight to the native input, so v-model can
    // stay empty and leave the submit button disabled. These three hooks cover
    // the ways a fill announces itself; syncAutofilledFields is idempotent.
    onNativeFieldEvent(e) {
      if (e.target && e.target.name in FIELD_BY_NAME) this.syncAutofilledFields();
    },
    onAutofillAnimation(e) {
      // Chrome fills without firing input/change; :-webkit-autofill restyling
      // is the only signal it gives. See the keyframes in <style>.
      if (e.animationName === "onAutofillStart") this.syncAutofilledFields();
    },
    syncAutofilledFields() {
      Object.keys(FIELD_BY_NAME).forEach((name) => {
        const input = this.$el.querySelector("input[name='" + name + "']");
        if (!input) return;

        const key = FIELD_BY_NAME[name];
        if (input.value === this[key]) return;

        this[key] = input.value;
        if (this.$v[key]) this.$v[key].$touch();
      });
    },
    getReferralCode() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const referral = urlParams.get('referral');
      this.referralCode = referral;
    }
  },
};
</script>
